import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Gift, Users, Trophy, Share2, Copy, 
  CheckCircle2, ArrowRight, Star, Zap,
  QrCode, Mail, Twitter, Facebook, ExternalLink,
  Watch, Lock, AlertCircle, Percent
} from 'lucide-react';

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subValue?: string;
}

const StatCard = ({ icon, label, value, subValue }: StatCardProps) => (
  <div className="bg-[#050505] border border-slate-800 p-6 rounded-[2.5rem] relative overflow-hidden group hover:border-cyan-500/50 transition-all duration-500">
    <div className="absolute top-0 right-0 p-8 opacity-[0.02] group-hover:opacity-[0.05] transition-opacity rotate-12 group-hover:rotate-0 transition-transform duration-700">
      {icon}
    </div>
    <div className="flex items-center gap-4 mb-4">
      <div className="p-3 bg-slate-900 rounded-2xl text-cyan-400 border border-slate-800 group-hover:shadow-[0_0_20px_rgba(0,255,255,0.2)] transition-all">
        {icon}
      </div>
      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{label}</span>
    </div>
    <div className="flex flex-col">
      <span className="text-3xl font-black text-white tracking-tighter uppercase italic">{value}</span>
      {subValue && <span className="text-[10px] font-bold text-cyan-500 mt-1 uppercase tracking-widest">{subValue}</span>}
    </div>
  </div>
);

export const ReferralDashboard = ({ user }: { user: any }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const referralCode = user?.referralCode || 'VITACORE';
  const referralLink = `${window.location.origin}?ref=${referralCode}`;
  
  const shareMessage = t('referral.share_message', { code: referralCode });
  
  // Get referral rewards stats
  const pendingRewards = user?.referralRewards?.pending || 0;
  const earnedRewards = user?.referralRewards?.earned || 0;

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOptions = [
    { icon: <Twitter size={18} />, labelKey: 'referral.share_twitter', color: 'hover:bg-[#1DA1F2]' },
    { icon: <Mail size={18} />, labelKey: 'referral.share_email', color: 'hover:bg-cyan-500' },
    { icon: <ExternalLink size={18} />, labelKey: 'referral.share_other', color: 'hover:bg-slate-700' }
  ];

  // Calculate milestones dynamically based on confirmedSubscriptions
  const confirmedSubscriptions = user?.referralStats?.confirmedSubscriptions || 0;
  const referralDiscountPercent = user?.referralStats?.currentDiscountPercent || 0;
  const referralDiscountLocked = user?.referralStats?.referralDiscountLocked || false;
  
  // CRITICAL: Check if discount is locked at 100% (hard stop - accumulation paused)
  // This is enforced at code level, not just UI
  const isMaxDiscountReached = referralDiscountPercent >= 100 && referralDiscountLocked;
  
  // Calculate milestones - milestone at 10 is now "Referral Discount"
  const milestones = [
    { count: 5, rewardKey: 'referral.milestone_premium', icon: <Star /> },
    { count: 10, rewardKey: 'referral.milestone_discount', icon: <Gift />, isDiscount: true }, // Changed from T-Shirt to Discount
    { count: 25, rewardKey: 'referral.milestone_watch', icon: <Watch /> }
  ].map(ms => ({
    ...ms,
    status: confirmedSubscriptions >= ms.count ? 'completed' : confirmedSubscriptions > 0 && confirmedSubscriptions < ms.count ? 'current' : 'locked'
  }));

  return (
    <div className="p-4 md:p-10 space-y-10 animate-fadeIn">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-[2px] bg-cyan-500 shadow-[0_0_10px_rgba(0,255,255,0.5)]" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-cyan-500">{t('referral.ecosystem_expansion')}</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter glow-text uppercase italic">
            {t('referral.emotional_title')}
          </h1>
          <p className="text-slate-500 font-medium text-sm md:text-base max-w-md uppercase tracking-tight">
            {t('referral.cooperative_subtitle')}
          </p>
          <p className="text-emerald-400 text-xs font-bold italic mt-2">
            {t('referral.no_break_streak')}
          </p>
        </div>

        <div className="bg-[#050505] border-2 border-slate-800 p-8 rounded-[3rem] flex flex-col items-center gap-6 shadow-2xl relative group overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="text-center relative z-10">
            <p className="text-[9px] font-black text-slate-500 uppercase tracking-[0.3em] mb-2">{t('referral.personal_code')}</p>
            <div className="text-3xl font-black text-cyan-400 tracking-[0.2em] uppercase italic bg-slate-900 px-8 py-3 rounded-2xl border border-slate-800 mb-4">
              {referralCode}
            </div>
            <button 
              onClick={handleCopy}
              className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl font-black uppercase tracking-widest text-xs transition-all active:scale-95 ${
                copied ? 'bg-green-500 text-black' : 'bg-white text-black hover:bg-cyan-400'
              }`}
            >
              {copied ? <CheckCircle2 size={18} /> : <Copy size={18} />}
              {copied ? t('referral.copied') : t('referral.copy_link')}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <StatCard 
          icon={<Users size={24} />} 
          label={t('referral.total_referrals')} 
          value={confirmedSubscriptions}
          subValue={`${confirmedSubscriptions > 0 ? Math.round((confirmedSubscriptions / 25) * 100) : 0}% ${t('referral.to_next_milestone')}`}
        />
        <StatCard 
          icon={<Zap size={24} />} 
          label={t('referral.pending')} 
          value={pendingRewards}
          subValue={t('referral.pending_desc')}
        />
        <StatCard 
          icon={<Trophy size={24} />} 
          label={t('referral.earned')} 
          value={earnedRewards}
          subValue={`${user?.referralStats?.totalXPEarned || 0} ${t('referral.earned_desc')}`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="bg-[#050505] border border-slate-800 p-10 rounded-[3rem] space-y-8">
          <h2 className="text-2xl font-black uppercase italic tracking-tighter">{t('referral.quick_share')}</h2>
          <div className="grid grid-cols-3 gap-4">
            {shareOptions.map((option, idx) => (
              <button 
                key={idx} 
                onClick={async () => {
                  try {
                    if (option.labelKey === 'referral.share_twitter') {
                      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}&url=${encodeURIComponent(referralLink)}`;
                      window.open(twitterUrl, '_blank');
                    } else if (option.labelKey === 'referral.share_email') {
                      const emailSubject = encodeURIComponent(t('referral.share_message', { code: referralCode }));
                      const emailBody = encodeURIComponent(`${shareMessage}\n\n${referralLink}`);
                      window.location.href = `mailto:?subject=${emailSubject}&body=${emailBody}`;
                    } else {
                      // Other - use Web Share API or copy to clipboard
                      if (navigator.share) {
                        await navigator.share({
                          title: t('referral.share_message', { code: referralCode }),
                          text: shareMessage,
                          url: referralLink
                        });
                      } else {
                        await navigator.clipboard.writeText(`${shareMessage}\n${referralLink}`);
                        alert(t('referral.copied'));
                      }
                    }
                  } catch (error: any) {
                    if (error.name !== 'AbortError') {
                      console.error('[REFERRAL] Share error:', error);
                    }
                  }
                }}
                className={`flex flex-col items-center gap-3 p-6 rounded-[2rem] bg-slate-900 border border-slate-800 transition-all active:scale-90 group ${option.color}`}
              >
                <div className="group-hover:scale-125 transition-transform duration-500">{option.icon}</div>
                <span className="text-[8px] font-black uppercase tracking-widest">{t(option.labelKey)}</span>
              </button>
            ))}
          </div>
          <div className="p-6 bg-cyan-500/5 border border-cyan-500/20 rounded-3xl">
            <p className="text-[10px] leading-relaxed text-slate-400 italic">
              " {shareMessage} "
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <h2 className="text-2xl font-black uppercase italic tracking-tighter">{t('referral.milestones')}</h2>
          
          {/* BLOCKING MESSAGE: Hard stop at 100% - accumulation paused until discount is used */}
          {isMaxDiscountReached && (
            <div className="bg-orange-500/10 border-2 border-orange-500/50 rounded-[2rem] p-6 space-y-3 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-3">
                  <Lock className="text-orange-400" size={20} />
                  <h3 className="text-sm font-black uppercase text-orange-400">{t('referral.max_discount_warning_title')}</h3>
                </div>
                <p className="text-xs text-slate-300 leading-relaxed mb-2">{t('referral.max_discount_warning')}</p>
                <p className="text-[10px] text-orange-400/70 uppercase tracking-widest font-bold italic">
                  {t('referral.accumulation_paused')}
                </p>
              </div>
            </div>
          )}
          
          {/* Current Discount Display */}
          {referralDiscountPercent > 0 && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-[2rem] p-6 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black uppercase tracking-widest text-slate-400">{t('referral.current_discount')}</span>
                <span className="text-2xl font-black text-emerald-400">{referralDiscountPercent}%</span>
              </div>
              {/* Progress bar: 0% to 100% */}
              <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400 transition-all"
                  style={{ width: `${Math.min(referralDiscountPercent, 100)}%` }}
                />
              </div>
              <p className="text-[10px] text-slate-500 uppercase tracking-widest">
                {t('referral.discount_rule', { count: confirmedSubscriptions, percent: referralDiscountPercent })}
              </p>
            </div>
          )}
          
          <div className="space-y-4">
            {milestones.map((ms, idx) => {
              const isCompleted = ms.status === 'completed';
              const isCurrent = ms.status === 'current';
              const isLocked = ms.status === 'locked';
              const progress = confirmedSubscriptions >= ms.count ? 100 : (confirmedSubscriptions / ms.count) * 100;
              
              // Special handling for discount milestone (count 10)
              const isDiscountMilestone = ms.isDiscount === true;
              const discountSubtitle = isDiscountMilestone && isCompleted 
                ? t('referral.discount_milestone_completed', { percent: referralDiscountPercent })
                : isDiscountMilestone && isCurrent
                ? t('referral.discount_milestone_progress', { current: confirmedSubscriptions, target: ms.count, percent: referralDiscountPercent })
                : isDiscountMilestone
                ? t('referral.discount_milestone_locked', { target: ms.count })
                : null;
              
              // Show lock icon if discount is locked at 100%
              const showLockIcon = isDiscountMilestone && referralDiscountLocked;
              
              return (
                <div key={idx} className={`flex items-center justify-between p-6 rounded-[2rem] border transition-all ${
                  isCompleted ? 'bg-emerald-500/10 border-emerald-500' : 
                  isCurrent ? 'bg-cyan-500/10 border-cyan-500' : 
                  'bg-[#050505] border-slate-800 opacity-50'
                }`}>
                  <div className="flex items-center gap-5">
                    <div className={`p-4 rounded-2xl ${
                      isCompleted ? 'bg-emerald-500 text-black' : 
                      isCurrent ? 'bg-cyan-500 text-black' : 
                      'bg-slate-900 text-slate-600'
                    }`}>
                      {ms.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-black uppercase tracking-widest text-white">{t(ms.rewardKey)}</p>
                        {isDiscountMilestone && (
                          <span className="text-[8px] font-bold text-slate-500 uppercase">{t('referral.discount_subtitle')}</span>
                        )}
                      </div>
                      {isDiscountMilestone && discountSubtitle ? (
                        <p className="text-[10px] font-bold text-slate-400 uppercase mt-1">{discountSubtitle}</p>
                      ) : (
                        <p className="text-[10px] font-bold text-slate-500 uppercase">
                          {isCompleted ? t('referral.completed') : t('referral.more_needed', { count: ms.count - confirmedSubscriptions })}
                        </p>
                      )}
                      {isCurrent && (
                        <div className="mt-2 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <div className="h-full bg-cyan-500 transition-all" style={{ width: `${progress}%` }} />
                        </div>
                      )}
                    </div>
                  </div>
                  {showLockIcon ? (
                    <div className="flex items-center gap-2 px-4 py-2 bg-orange-500/20 rounded-full text-[8px] font-black text-orange-400 uppercase tracking-widest">
                      <Lock size={12} />
                      {t('referral.locked')}
                    </div>
                  ) : isCompleted ? (
                    <div className="px-4 py-2 bg-emerald-500/20 rounded-full text-[8px] font-black text-emerald-400 uppercase tracking-widest">{t('referral.claimed')}</div>
                  ) : isCurrent ? (
                    <div className="px-4 py-2 bg-cyan-500/20 rounded-full text-[8px] font-black text-cyan-400 uppercase tracking-widest">{t('referral.in_progress')}</div>
                  ) : (
                    <Lock size={16} className="text-slate-800" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

